import { useEffect, useState } from "react";
import { useBranding } from "@/lib/branding";
import { endpoints } from "@/lib/api";
import { ArrowUp, ArrowDown, MapPin } from "lucide-react";

// Warehouse Command Center — global inventory heatmap by region
// Sprint 2 of the Lifecycle Intelligence build.

const REGION_COORDS = {
  EMEA: { label: "EMEA", tint: "#1E4FD8" },
  AMER: { label: "AMER", tint: "#0F766E" },
  APAC: { label: "APAC", tint: "#B45309" },
};

export default function AdminWarehouse() {
  const { brand } = useBranding();
  const [data, setData] = useState(null);

  useEffect(() => {
    endpoints.lifecycleWarehouse().then(setData).catch(() => {});
  }, []);

  const brandBlue = brand?.primary || "#0033A0";
  const brandSoft = brand?.primarySoft || "#0033A020";

  return (
    <div className="max-w-[1180px] mx-auto" data-testid="warehouse-page">
      {/* Title */}
      <div className="mb-10">
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-3" style={{ color: brandBlue }}>
          Warehouse Command Center
        </div>
        <h1 className="text-4xl font-semibold tracking-tight text-slate-900 leading-[1.15]">
          Every device in every warehouse. In one view.
        </h1>
        <p className="text-slate-500 mt-3 max-w-2xl text-[15px] leading-7">
          Global inventory across regions, blended with returns pipeline and surplus stock — the source of truth the Sourcing Agent draws on.
        </p>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-12">
        {data?.kpis?.map((k) => (
          <div key={k.label} className="rounded-2xl bg-white p-6" style={{ border: `1px solid ${brandSoft}` }}>
            <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-2">{k.label}</div>
            <div className="text-3xl font-semibold text-slate-900">{k.value}</div>
            <div className="flex items-center gap-1 text-xs mt-2 text-emerald-700">
              {k.trend === "up" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              <span className="font-semibold">good trajectory</span>
            </div>
          </div>
        ))}
      </div>

      {/* Region strip */}
      <div className="rounded-2xl bg-white p-8 mb-6" style={{ border: `1px solid ${brandSoft}` }}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-1">Global inventory</div>
            <div className="text-lg font-semibold text-slate-900">
              {data ? `${data.total_units.toLocaleString()} units · ${data.sites} sites` : "—"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data?.regions?.map((r) => {
            const meta = REGION_COORDS[r.region] || { label: r.region, tint: brandBlue };
            const readyPct = r.qty ? Math.round((r.ready / r.qty) * 100) : 0;
            return (
              <div key={r.region} className="rounded-xl p-5 relative overflow-hidden" style={{ border: `1px solid ${brandSoft}` }} data-testid={`warehouse-region-${r.region}`}>
                <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: meta.tint }} />
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={14} style={{ color: meta.tint }} />
                  <div className="text-sm font-semibold" style={{ color: meta.tint }}>{meta.label}</div>
                </div>
                <div className="text-3xl font-semibold text-slate-900">{r.qty.toLocaleString()}</div>
                <div className="text-xs text-slate-500 mt-1">units · {r.sites} sites</div>
                <div className="flex justify-between text-xs mt-4 pt-4 border-t border-slate-100">
                  <span className="text-slate-500">Ready: <span className="font-semibold text-slate-900">{r.ready.toLocaleString()}</span></span>
                  <span className="text-slate-500">Returns: <span className="font-semibold text-slate-900">{r.returns.toLocaleString()}</span></span>
                  <span className="text-slate-500">Surplus: <span className="font-semibold text-slate-900">{r.surplus.toLocaleString()}</span></span>
                </div>
                <div className="mt-4 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${readyPct}%`, backgroundColor: meta.tint }} />
                </div>
                <div className="text-[10px] text-slate-400 mt-1">{readyPct}% ready-to-deploy</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Locations table */}
      <div className="rounded-2xl bg-white p-8" style={{ border: `1px solid ${brandSoft}` }}>
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-5">Location detail</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="pb-3 font-semibold">Location</th>
                <th className="pb-3 font-semibold">Legal entity</th>
                <th className="pb-3 font-semibold">Model</th>
                <th className="pb-3 font-semibold">Status</th>
                <th className="pb-3 font-semibold text-right">Qty</th>
              </tr>
            </thead>
            <tbody>
              {data?.locations?.map((l, i) => (
                <tr key={i} className="border-b border-slate-50" data-testid={`warehouse-row-${i}`}>
                  <td className="py-3 font-medium text-slate-900">{l.location}</td>
                  <td className="py-3 text-slate-500 font-mono text-xs">{l.legal_entity}</td>
                  <td className="py-3 text-slate-500">{l.model}</td>
                  <td className="py-3">
                    <span className="text-xs px-2 py-1 rounded-md" style={{ backgroundColor: brandSoft, color: brandBlue }}>
                      {l.status}
                    </span>
                  </td>
                  <td className="py-3 text-right font-semibold text-slate-900">{l.qty.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
